let loadPromise: Promise<void> | null = null;

/** Load the YouTube IFrame Player API once per page. */
export function loadYouTubeApi(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube API requires a browser"));
  }
  if (window.YT?.Player) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };

    if (window.YT?.Player) {
      resolve();
      return;
    }

    const existing = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]',
    );
    if (existing) return;

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    tag.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load YouTube player"));
    };
    document.head.appendChild(tag);
  });

  return loadPromise;
}

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

export interface YouTubeVideoInfo {
  title: string;
  author: string;
}

export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

export interface CaptionTrackOption {
  languageCode: string;
  label: string;
  track: YT.CaptionTrack;
}

function captionTrackKey(track: YT.CaptionTrack): string {
  return `${track.languageCode}:${track.kind ?? ""}:${track.vss_id ?? ""}`;
}

export function ensureCaptionsModule(player: YT.Player): void {
  try {
    player.loadModule?.("captions");
  } catch {
    /* captions unavailable */
  }
}

export function readVideoInfo(player: YT.Player): YouTubeVideoInfo | null {
  try {
    const data = player.getVideoData();
    if (!data?.title) return null;
    return { title: data.title, author: data.author || "" };
  } catch {
    return null;
  }
}

export function readCaptionTracks(player: YT.Player): CaptionTrackOption[] {
  try {
    const list = player.getOption<YT.CaptionTrack[]>("captions", "tracklist");
    if (!Array.isArray(list)) return [];
    const seen = new Set<string>();
    const tracks: CaptionTrackOption[] = [];
    for (const track of list) {
      if (!track?.languageCode) continue;
      const key = captionTrackKey(track);
      if (seen.has(key)) continue;
      seen.add(key);
      tracks.push({
        languageCode: track.languageCode,
        label: track.displayName || track.languageName || track.languageCode,
        track,
      });
    }
    return tracks;
  } catch {
    return [];
  }
}

export function readActiveCaptionTrack(
  player: YT.Player,
): CaptionTrackOption | null {
  try {
    const track = player.getOption<YT.CaptionTrack>("captions", "track");
    if (!track?.languageCode) return null;
    return {
      languageCode: track.languageCode,
      label: track.displayName || track.languageName || track.languageCode,
      track,
    };
  } catch {
    return null;
  }
}

export function setCaptionEnabled(
  player: YT.Player,
  enabled: boolean,
  track?: CaptionTrackOption | null,
): boolean {
  try {
    ensureCaptionsModule(player);
    if (!enabled) {
      player.setOption("captions", "track", {});
      return true;
    }

    const candidates: unknown[] = [];
    if (track?.track) candidates.push(track.track);
    if (track?.languageCode) candidates.push({ languageCode: track.languageCode });
    for (const option of readCaptionTracks(player)) {
      candidates.push(option.track);
    }

    for (const candidate of candidates) {
      const languageCode = (candidate as YT.CaptionTrack)?.languageCode;
      if (!languageCode) continue;
      player.setOption("captions", "track", candidate);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

export function formatPlaybackRate(rate: number): string {
  return rate === 1 ? "1×" : `${rate}×`;
}
